import { useState, useMemo, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Job, JobStatus } from '@core/generated/gql-types';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
} from '@core/util/jobScheduleBounds.util';
import { doesJobWorkInDateRange } from '@/modules/jobs/utils/jobFilters.utils';

export type SortField =
  | 'status'
  | 'description'
  | 'title'
  | 'startDate'
  | 'endDate'
  | 'startTime'
  | 'endTime'
  | 'client'
  | 'site'
  | 'workType'
  | 'candidate'
  | 'postedDate'
  | null;

export type SortDirection = 'asc' | 'desc';

export const useJobsTable = (
  jobs: Job[],
  timeframeStartDate: Dayjs | null = null,
  timeframeEndDate: Dayjs | null = null,
) => {
  const [sortField, setSortField] = useState<SortField>('startDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<JobStatus[]>([]);
  const [siteFilters, setSiteFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // If timeframeStartDate is provided or modified, set startDate
  // and endDate for job filtering.
  useEffect(() => {
    if (!timeframeStartDate) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    setStartDate(timeframeStartDate);

    if (timeframeEndDate) {
      setEndDate(timeframeEndDate);
    } else {
      setEndDate(dayjs(timeframeStartDate).add(6, 'day'));
    }
  }, [timeframeStartDate, timeframeEndDate, setStartDate, setEndDate]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const allFilteredJobs = useMemo(() => {
    return (
      jobs
        ?.filter((job) => {
          if (!job) return false;

          const title = job.title?.toLowerCase() || '';

          const status = job.itemStatus || '';

          const query = searchQuery.toLowerCase();

          const matchesSearch = title.includes(query) || status.includes(query);

          const matchesStatus =
            statusFilters.length === 0 ||
            (job.itemStatus && statusFilters.includes(job.itemStatus));

          const matchesSite =
            siteFilters.length === 0 ||
            (job.site?.name != null && siteFilters.includes(job.site.name));

          const rangeEnd = endDate ?? startDate;
          const worksInRange =
            startDate == null ||
            rangeEnd == null ||
            doesJobWorkInDateRange(
              job,
              startDate.startOf('day').toDate(),
              rangeEnd.endOf('day').toDate(),
            );

          return matchesSearch && matchesStatus && matchesSite && worksInRange;
        })
        ?.sort((a, b) => {
          if (!sortField) return 0;

          const direction = sortDirection === 'asc' ? 1 : -1;

          switch (sortField) {
            case 'status':
              return (
                direction *
                (a.itemStatus || '').localeCompare(b.itemStatus || '')
              );
            case 'description':
              return (
                direction *
                (a.description || '').localeCompare(b.description || '')
              );
            case 'title':
              return direction * (a.title || '').localeCompare(b.title || '');
            case 'startDate': {
              const aDay = getJobDisplayScheduleStartDay(a);
              const bDay = getJobDisplayScheduleStartDay(b);
              const av = aDay?.valueOf() ?? 0;
              const bv = bDay?.valueOf() ?? 0;
              return direction * (av - bv);
            }
            case 'endDate': {
              const aDay = getJobDisplayScheduleEndDay(a);
              const bDay = getJobDisplayScheduleEndDay(b);
              const av = aDay?.valueOf() ?? 0;
              const bv = bDay?.valueOf() ?? 0;
              return direction * (av - bv);
            }
            case 'startTime': {
              const aTime = a.days?.startTime || a.clockInTime || '';
              const bTime = b.days?.startTime || b.clockInTime || '';
              return direction * aTime.localeCompare(bTime);
            }
            case 'endTime': {
              const aTime = a.days?.endTime || a.clockOutTime || '';
              const bTime = b.days?.endTime || b.clockOutTime || '';
              return direction * aTime.localeCompare(bTime);
            }
            case 'client':
              return (
                direction * (a.clientId || '').localeCompare(b.clientId || '')
              );
            case 'site':
              return (
                direction *
                (a.site?.name || '').localeCompare(b.site?.name || '')
              );
            case 'workType':
              return (
                direction * (a.workType || '').localeCompare(b.workType || '')
              );
            case 'candidate':
              // Sort by userId (worker ID) - actual name sorting would need worker store
              return direction * (a.userId || '').localeCompare(b.userId || '');
            case 'postedDate':
              return (
                direction * (a.createdAt || '').localeCompare(b.createdAt || '')
              );
            default:
              return 0;
          }
        }) ?? []
    );
  }, [
    jobs,
    searchQuery,
    statusFilters,
    siteFilters,
    sortField,
    sortDirection,
    startDate,
    endDate,
  ]);

  // Calculate pagination
  const totalItems = allFilteredJobs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFilteredJobs = allFilteredJobs.slice(startIndex, endIndex);

  // After filters shrink the list, avoid showing an empty page (e.g. page 2 with no rows).
  useEffect(() => {
    const maxPage = Math.max(1, totalPages);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    searchQuery,
    setSearchQuery,
    statusFilters,
    setStatusFilters,
    siteFilters,
    setSiteFilters,
    startDate,
    endDate,
    handleSort,
    allFilteredJobs,
    paginatedFilteredJobs,
    pagination: {
      currentPage,
      totalPages,
      onPageChange: handlePageChange,
      itemsPerPage,
      onItemsPerPageChange: handleItemsPerPageChange,
      startIndex: (currentPage - 1) * itemsPerPage + 1,
      endIndex: Math.min(
        (currentPage - 1) * itemsPerPage + itemsPerPage,
        totalItems,
      ),
      totalItems,
      hasMore: endIndex < totalItems,
    },
  };
};
